// src/components/OrganizationSelector.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger";
import "./OrganizationSelector.css";

/**
 * Full-page component for selecting an organization.
 * Handles 0/1/multiple organizations with smart navigation logic.
 */
function OrganizationSelector({
  organizations,
  onSelectOrganization,
  onCancel,
  onCreateOrganization,
  onManageOrganizations,
  userRoles,
}) {
  const { logout } = useAuth();
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Helper to check role (supports both string and object formats)
  const hasRole = (roleName) =>
    userRoles?.some(
      (role) => (typeof role === "string" ? role : role?.name) === roleName
    );

  logger.log("[OrganizationSelector] Rendered with props:", {
    organizationsCount: organizations.length,
    hasOnCreateOrganization: !!onCreateOrganization,
    onCreateOrganization: typeof onCreateOrganization,
    onManageOrganizations: !!onManageOrganizations,
    userRoles: userRoles,
    hasFullAdmin: hasRole("Full Admin"),
    hasOrgAdmin: hasRole("Organization Admin"),
  });

  // ESC key handler - go back to app if available and organizations have installations
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && onCancel) {
        // Only go back if at least one organization has installations
        const hasInstallations = organizations.some(
          (org) => org.installations && org.installations.length > 0
        );
        if (hasInstallations) {
          onCancel();
        }
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onCancel, organizations]);

  const handleCopyId = (e, orgId) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    navigator.clipboard.writeText(orgId);
    setCopiedId(orgId);
    setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
  };

  if (organizations.length === 0) {
    // Only Full Admin and Organization Admin can create organizations
    const canCreateOrganization =
      onCreateOrganization &&
      (hasRole("Full Admin") || hasRole("Organization Admin"));

    return (
      <div className="organization-selector-container">
        <div className="organization-selector-card">
          <h2>No Organizations Found</h2>
          <p className="error-message">
            Your account is not associated with any organizations.
          </p>
          {canCreateOrganization && (
            <p className="error-message">
              To create one,{" "}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.log("[OrganizationSelector] Create button clicked");
                  logger.log(
                    "[OrganizationSelector] onCreateOrganization:",
                    onCreateOrganization
                  );
                  if (onCreateOrganization) {
                    logger.log(
                      "[OrganizationSelector] Calling onCreateOrganization"
                    );
                    onCreateOrganization();
                  }
                }}
                className="create-link-button"
              >
                click here to create an organization
              </button>
            </p>
          )}
          <p className="error-message">
            <a
              href="https://developersupport.allegion.com/hc/en-us/requests"
              target="_blank"
              rel="noopener noreferrer"
              className="support-link"
            >
              Please open a ticket with the Allegion Developer Support team for
              access to this app
            </a>
          </p>
          <button className="back-button" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  // Filter organizations based on search term
  const filteredOrganizations = organizations.filter((org) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      org.name.toLowerCase().includes(search) ||
      org.orgId.toLowerCase().includes(search)
    );
  });

  return (
    <div className="organization-selector-container">
      <div className="organization-selector-card">
        <h2>Select Organization</h2>
        <p className="subtitle">Choose an organization to continue</p>

        {/* Search Box */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search organizations by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="search-clear-button"
              onClick={() => setSearchTerm("")}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="organization-list">
          {[...filteredOrganizations]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((org) => (
              <div
                key={org.orgId}
                className="organization-item"
                onClick={() => onSelectOrganization(org)}
              >
                <div className="organization-name">{org.name}</div>
                <div className="organization-id-row">
                  <div className="organization-id">ID: {org.orgId}</div>
                  <button
                    className="copy-id-button"
                    onClick={(e) => handleCopyId(e, org.orgId)}
                    title="Copy Organization ID"
                  >
                    {copiedId === org.orgId ? "✓" : "📋"}
                  </button>
                </div>
                <div className="organization-installations">
                  {org.installations.length} installation
                  {org.installations.length !== 1 ? "s" : ""}
                </div>
              </div>
            ))}
        </div>

        <div className="button-group">
          {onManageOrganizations &&
            (hasRole("Full Admin") || hasRole("Organization Admin")) && (
              <button className="manage-button" onClick={onManageOrganizations}>
                Go To Manage Organizations
              </button>
            )}
          {onCancel && (
            <button className="back-button" onClick={onCancel}>
              Back to App
            </button>
          )}
          {!onCancel && (
            <button className="logout-button" onClick={logout}>
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrganizationSelector;
